import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppIconButton from "@/common/components/Button/AppIconButton";
import AccessoryCheckLink from "@/common/components/Entity/AccessoryCheck/AccessoryCheckLink";
import AssessmentFlowLink from "@/common/components/Entity/AssessmentFlow/AssessmentFlowLink";
import AssetLink from "@/common/components/Entity/Asset/AssetLink";
import AssetSubscriptionLink from "@/common/components/Entity/AssetSubscription/AssetSubscriptionLink";
import AssetSubscriptionPlanLink from "@/common/components/Entity/AssetSubscriptionPlan/AssetSubscriptionPlanLink";
import ContractLink from "@/common/components/Entity/Contract/ContractLink";
import CustomerLink from "@/common/components/Entity/Customer/CustomerLink";
import DamageCostEvaluationLink from "@/common/components/Entity/DamageCostEvaluation/DamageCostEvaluationLink";
import DamageCostEvaluationAggregateLink from "@/common/components/Entity/DamageCostEvaluationAggregate/DamageCostEvaluationAggregateLink";
import DamageDetectionLink from "@/common/components/Entity/DamageDetection/DamageDetectionLink";
import DamageDetectionAggregateLink from "@/common/components/Entity/DamageDetectionAggregate/DamageDetectionAggregateLink";
import DepartmentLink from "@/common/components/Entity/Department/DepartmentLink";
import LocationLink from "@/common/components/Entity/Location/LocationLink";
import PoolLink from "@/common/components/Entity/Pool/PoolLink";
import ProductLocationLink from "@/common/components/Entity/ProductLocation/ProductLocationLink";
import RepairOperationLink from "@/common/components/Entity/RepairOperation/RepairOperationLink";
import SpotLink from "@/common/components/Entity/Spot/SpotLink";
import TenantRequestLink from "@/common/components/Entity/TenantRequest/TenantRequestLink";
import VehicleLink from "@/common/components/Entity/Vehicle/VehicleLink";
import VisualInspectionLink from "@/common/components/Entity/VisualInspection/VisualInspectionLink";
import { BaseEntityInlineInheritableProps } from "@/common/components/Entity/components/EntityInline/BaseEntityInline";
import AppIcon, { AppIconProps } from "@/common/components/Icons/AppIcon";
import InvoiceLink from "@/common/components/Link/InvoiceLink";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useOnOutsideClick } from "@/common/hooks/useOnOutsideClick";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { tenantService } from "@/common/services/tenant";
import { appCommonConfig } from "@/config/config";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, GlobalSearchCategory } from "@/core/api/generated/v1";

import CategoryBlock from "./CategoryBlock";

const defaultInlineProps: Partial<BaseEntityInlineInheritableProps> = {
  withIcon: true,
  withTooltip: false,
};

interface Props {
  sx?: BoxProps["sx"];
}

export default function GlobalSearch({ sx }: Props) {
  const tenantInfo = tenantService.resolveTenant();
  const { hasPermissions } = useAuthorizationInfo();

  const [searchValue, setSearchValue] = useState("");
  const minSearchLength = 3;
  const [searchCategory, setSearchCategory] = useState<GlobalSearchCategory>(
    GlobalSearchCategory.All,
  );
  const [isPanelOpened, setIsPanelOpened] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const limitPerCategory = 10;
  const initialLimitAndIncreaseStep = 25;
  const [categoryLimit, setCategoryLimit] = useState<number>(initialLimitAndIncreaseStep);

  const request = useApiRequest(
    apiClient.globalSearchApi.apiV1SearchGlobalGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      offset: 0,
      limit: categoryLimit,
      limitPerCategory: limitPerCategory,
      search: searchValue,
      category: searchCategory,
    },
    {
      skip: true,
      deps: [categoryLimit], // if searchCategory added: request faster then limit change
      debouncedDeps: {
        skip: searchValue.length < minSearchLength,
        deps: [categoryLimit, searchValue],
        wait: 500,
        options: { leading: false, trailing: true },
      },
    },
  );

  const searchResult = useMemo(
    () => (searchValue.length < minSearchLength ? null : request?.data),
    [request?.data, searchValue],
  );

  //searchCategory change handle
  useEffect(() => {
    if (searchValue.length >= minSearchLength) {
      setCategoryLimit((prevState) => {
        prevState === initialLimitAndIncreaseStep && request.refetch();
        return initialLimitAndIncreaseStep;
      });
    }
  }, [searchCategory]);

  const closePanel = useCallback(() => setIsPanelOpened(false), [setIsPanelOpened]);

  const panelRef = useRef<HTMLElement>(null!);
  useOnOutsideClick(panelRef, () => {
    closePanel();
  });

  const searchCategoryItems = [
    {
      category: GlobalSearchCategory.All,
      name: enumService.getEnumValueName(
        ApiEnumName.GlobalSearchCategory,
        GlobalSearchCategory.All,
      ),
    },
    ...(hasPermissions([AppPermission.VehicleRead])
      ? [
          {
            category: GlobalSearchCategory.Vehicles,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Vehicles,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => <AppIcon {...iconProps} of='vehicle' />,
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.ContractRead])
      ? [
          {
            category: GlobalSearchCategory.Contracts,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Contracts,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => <AppIcon {...iconProps} of='contract' />,
          },
        ]
      : []),
    // NB: temporary disabled
    // ...(hasPermissions([AppPermission.DocumentRead])
    //   ? [
    //       {
    //         category: GlobalSearchCategory.Documents,
    //         name: enumService.getEnumValueName(ApiEnumName.GlobalSearchCategory, GlobalSearchCategory.Documents),
    //       },
    //     ]
    //   : []),
    ...(hasPermissions([AppPermission.VisualInspectionRead])
      ? [
          {
            category: GlobalSearchCategory.VisualInspections,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.VisualInspections,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='visualInspection' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.DamageDetectionRead])
      ? [
          {
            category: GlobalSearchCategory.DamageDetections,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.DamageDetections,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='damageDetection' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.DamageDetectionRead])
      ? [
          {
            category: GlobalSearchCategory.DamageDetectionAggregates,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.DamageDetectionAggregates,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='damageDetectionAggregate' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.InvoiceRead])
      ? [
          {
            category: GlobalSearchCategory.Invoices,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Invoices,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => <AppIcon {...iconProps} of='invoice' />,
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.AccessoryCheckRead])
      ? [
          {
            category: GlobalSearchCategory.AccessoryChecks,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.AccessoryChecks,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='accessoryCheck' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.AssetRead])
      ? [
          {
            category: GlobalSearchCategory.Assets,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Assets,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => <AppIcon {...iconProps} of='asset' />,
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.CustomerRead])
      ? [
          {
            category: GlobalSearchCategory.Customers,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Customers,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => <AppIcon {...iconProps} of='customer' />,
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.AssessmentFlowRead])
      ? [
          {
            category: GlobalSearchCategory.AssessmentFlows,
            name: "Damages & approvals",
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='assessmentFlow' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.DamageCostEvaluationRead])
      ? [
          {
            category: GlobalSearchCategory.DamageCostEvaluations,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.DamageCostEvaluations,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='damageCostEvaluation' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.DamageCostEvaluationRead])
      ? [
          {
            category: GlobalSearchCategory.DamageCostEvaluationAggregates,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.DamageCostEvaluationAggregates,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='damageCostEvaluationAggregate' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.RepairOperationRead])
      ? [
          {
            category: GlobalSearchCategory.RepairOperations,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.RepairOperations,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='repairOperation' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.DepartmentRead])
      ? [
          {
            category: GlobalSearchCategory.Departments,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Departments,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='department' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.LocationRead])
      ? [
          {
            category: GlobalSearchCategory.Locations,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Locations,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => <AppIcon {...iconProps} of='location' />,
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.SpotRead])
      ? [
          {
            category: GlobalSearchCategory.Spots,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Spots,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => <AppIcon {...iconProps} of='spot' />,
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.ProductLocationRead])
      ? [
          {
            category: GlobalSearchCategory.ProductLocations,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.ProductLocations,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='productLocation' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.PoolRead])
      ? [
          {
            category: GlobalSearchCategory.Pools,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.Pools,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => <AppIcon {...iconProps} of='pool' />,
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.AssetSubscriptionPlanRead])
      ? [
          {
            category: GlobalSearchCategory.AssetSubscriptionPlans,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.AssetSubscriptionPlans,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='assetSubscriptionPlan' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.AssetSubscriptionRead])
      ? [
          {
            category: GlobalSearchCategory.AssetSubscriptions,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.AssetSubscriptions,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='assetSubscription' />
            ),
          },
        ]
      : []),
    ...(hasPermissions([AppPermission.TenantRequestRead])
      ? [
          {
            category: GlobalSearchCategory.TenantRequests,
            name: enumService.getEnumValueName(
              ApiEnumName.GlobalSearchCategory,
              GlobalSearchCategory.TenantRequests,
            ),
            getIcon: (iconProps: Partial<AppIconProps>) => (
              <AppIcon {...iconProps} of='tenantRequest' />
            ),
          },
        ]
      : []),
  ];

  const selectResCountInfoByCategory = (category: GlobalSearchCategory) => {
    if (!searchResult) {
      return {
        count: 0,
        totalCount: 0,
      };
    }
    switch (category) {
      case GlobalSearchCategory.All:
        return {
          count: searchResult.totalReturnedCount || 0,
          totalCount: searchResult.totalReturnedCount || 0,
        };
      case GlobalSearchCategory.Vehicles:
        return {
          count: searchResult.vehicles?.items?.length || 0,
          totalCount: searchResult.vehicles?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Contracts:
        return {
          count: searchResult.contracts?.items?.length || 0,
          totalCount: searchResult.contracts?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Documents:
        return {
          count: searchResult.documents?.items?.length || 0,
          totalCount: searchResult.documents?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.VisualInspections:
        return {
          count: searchResult.visualInspections?.items?.length || 0,
          totalCount: searchResult.visualInspections?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.DamageDetections:
        return {
          count: searchResult.damageDetections?.items?.length || 0,
          totalCount: searchResult.damageDetections?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.DamageDetectionAggregates:
        return {
          count: searchResult.damageDetectionAggregates?.items?.length || 0,
          totalCount: searchResult.damageDetectionAggregates?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Invoices:
        return {
          count: searchResult.invoices?.items?.length || 0,
          totalCount: searchResult.invoices?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.AccessoryChecks:
        return {
          count: searchResult.accessoryChecks?.items?.length || 0,
          totalCount: searchResult.accessoryChecks?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Assets:
        return {
          count: searchResult.assets?.items?.length || 0,
          totalCount: searchResult.assets?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Customers:
        return {
          count: searchResult.customers?.items?.length || 0,
          totalCount: searchResult.customers?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.AssessmentFlows:
        return {
          count: searchResult.assessmentFlows?.items?.length || 0,
          totalCount: searchResult.assessmentFlows?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.DamageCostEvaluations:
        return {
          count: searchResult.damageCostEvaluations?.items?.length || 0,
          totalCount: searchResult.damageCostEvaluations?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.DamageCostEvaluationAggregates:
        return {
          count: searchResult.damageCostEvaluationAggregates?.items?.length || 0,
          totalCount: searchResult.damageCostEvaluationAggregates?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.RepairOperations:
        return {
          count: searchResult.repairOperations?.items?.length || 0,
          totalCount: searchResult.repairOperations?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Departments:
        return {
          count: searchResult.departments?.items?.length || 0,
          totalCount: searchResult.departments?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Locations:
        return {
          count: searchResult.locations?.items?.length || 0,
          totalCount: searchResult.locations?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Spots:
        return {
          count: searchResult.spots?.items?.length || 0,
          totalCount: searchResult.spots?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.ProductLocations:
        return {
          count: searchResult.productLocations?.items?.length || 0,
          totalCount: searchResult.productLocations?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.Pools:
        return {
          count: searchResult.pools?.items?.length || 0,
          totalCount: searchResult.pools?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.AssetSubscriptionPlans:
        return {
          count: searchResult.assetSubscriptionPlans?.items?.length || 0,
          totalCount: searchResult.assetSubscriptionPlans?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.AssetSubscriptions:
        return {
          count: searchResult.assetSubscriptions?.items?.length || 0,
          totalCount: searchResult.assetSubscriptions?.pagination?.totalCount || 0,
        };
      case GlobalSearchCategory.TenantRequests:
        return {
          count: searchResult.tenantRequests?.items?.length || 0,
          totalCount: searchResult.tenantRequests?.pagination?.totalCount || 0,
        };
      default:
        return {
          count: 0,
          totalCount: 0,
        };
    }
  };

  const shouldShowCategoryResult = (category: GlobalSearchCategory) => {
    return searchCategory === GlobalSearchCategory.All ? true : searchCategory === category;
  };

  if (!tenantInfo) {
    return null;
  }

  const handleLoadMore = (category?: GlobalSearchCategory) => {
    if (!request.isLoading) {
      if (searchCategory === GlobalSearchCategory.All) setSearchCategory(category!);
      else setCategoryLimit(categoryLimit + initialLimitAndIncreaseStep);
    }
  };

  const loadMoreButton = (category: GlobalSearchCategory) => {
    const currentLimit =
      searchCategory === GlobalSearchCategory.All ? limitPerCategory : categoryLimit;
    const { totalCount, count } = selectResCountInfoByCategory(category) || {};
    const remainingCount = totalCount ? totalCount - count : 0;

    return (
      currentLimit < (totalCount || 0) && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            sx={{ pl: 2 }}
            variant='text'
            size='small'
            color='text'
            startIcon={<AppIcon of='add' />}
            onClick={() => handleLoadMore(category)}
          >
            More ({remainingCount})
          </Button>
        </Box>
      )
    );
  };

  return (
    <Box
      ref={panelRef}
      sx={{
        position: isPanelOpened ? { xs: "absolute", sm: "relative" } : "relative",
        top: 0,
        left: 0,
        zIndex: (th) => (isPanelOpened ? th.zIndex.modal : "auto"),
        width: "100%",
        ml: !isPanelOpened ? "auto" : "",
        backgroundColor: "inherit",
        borderRadius: (th) => th.shapeCustom.borderRadiusCard,
      }}
    >
      <TextField
        inputRef={searchInputRef}
        sx={{
          mb: 0,
          width: "100%",
          "& .MuiInputBase-root": {
            // pl: 1,
          },
        }}
        fullWidth={isPanelOpened}
        size='small'
        placeholder={`Search in ${appCommonConfig.appNameTitleCase}`}
        value={searchValue}
        onFocus={() => setIsPanelOpened(true)}
        onChange={(e) => setSearchValue(e.target.value)}
        slotProps={{
          inputLabel: { disabled: !isPanelOpened },
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                {!request.isLoading ? (
                  <AppIconButton
                    size='small'
                    tooltipProps={{ title: "Search", isDefaultEnterDelays: true }}
                    onClick={() => {
                      setIsPanelOpened(true);
                      searchInputRef.current?.focus();
                    }}
                  >
                    <AppIcon of='search' />
                  </AppIconButton>
                ) : (
                  <AppIconButton size='small'>
                    <CircularProgress
                      sx={{
                        animation: "none",
                      }}
                      variant='indeterminate'
                      color='inherit'
                      size={22}
                    />
                  </AppIconButton>
                )}
              </InputAdornment>
            ),
            endAdornment: isPanelOpened ? (
              <InputAdornment position='end'>
                <AppIconButton
                  size='small'
                  tooltipProps={{ title: "Clear search", isDefaultEnterDelays: true }}
                  onClick={() => {
                    setSearchValue("");
                    setSearchCategory(GlobalSearchCategory.All);
                    searchInputRef.current?.focus();
                  }}
                >
                  <AppIcon of='close' />
                </AppIconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      {isPanelOpened && (
        <Box
          sx={{
            position: "absolute",
            left: { xs: 0, sm: "unset" },
            height: "auto",
            maxHeight: "70vh",
            width: "100%",
            overflowY: "auto",
            backgroundColor: (th) => th.palette.background.paper,
            // border: (th) => `2px solid ${th.palette.background.gray}`,
            borderRadius: (th) => th.shapeCustom.borderRadiusCard,
            boxShadow: "2px 2px 5px 0px rgba(0, 0, 0, 0.2)",
            zIndex: 100,
            p: 2,
            pt: 0,
          }}
        >
          {/* Categories */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: (th) => th.palette.background.paper,
              py: 1,
              borderRadius: (th) => th.shape,
            }}
          >
            {searchCategoryItems.map((item) => {
              const countInfo = selectResCountInfoByCategory(item.category);
              return (
                <Button
                  key={item.category}
                  sx={{
                    m: 0.2,
                    fontWeight:
                      searchCategory === item.category || countInfo.totalCount > 0
                        ? "bold"
                        : "normal",
                  }}
                  variant={searchCategory === item.category ? "contained" : "outlined"}
                  color={searchCategory === item.category ? "secondary" : "text"}
                  size='small'
                  startIcon={item.getIcon?.({})}
                  onClick={() => setSearchCategory(item.category)}
                >
                  {item.name}
                  {searchResult ? ` (${countInfo.totalCount})` : ""}
                </Button>
              );
            })}
          </Box>

          {!searchResult && request.isLoading && (
            <Stack spacing={1}>
              {Array.from({ length: 3 }).map((x, i) => (
                <Stack spacing={0.5} key={i}>
                  <Skeleton variant='rectangular' width={"25%"} height={14} />
                  <Skeleton variant='rectangular' width={"100%"} height={40} />
                </Stack>
              ))}
            </Stack>
          )}

          {searchResult && (
            <Stack direction='column' spacing={2} sx={{ p: 1 }}>
              <AuthorizedElement permissions={[AppPermission.VehicleRead]}>
                {searchResult?.vehicles &&
                  searchResult?.vehicles?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Vehicles) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Vehicles) || undefined}
                      name='Vehicles'
                      items={searchResult?.vehicles?.items || []}
                    >
                      {(item) => (
                        <VehicleLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.ContractRead]}>
                {searchResult?.contracts &&
                  searchResult?.contracts?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Contracts) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Contracts) || undefined}
                      name='Contracts'
                      items={searchResult?.contracts?.items || []}
                    >
                      {(item) => (
                        <ContractLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              {/* NB: temporary disabled */}
              {/* <AuthorizedElement permissions={[AppPermission.DocumentRead]}>
                {searchResult?.documents  &&
                  searchResult?.documents?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Documents) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Documents) || undefined}
                      name='Documents'
                      items={searchResult?.documents?.items || []}
                    >
                      {(item) => (
                        <DocumentLink
                          document={item}
                          withIcon
                          withTooltip={false}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement> */}

              <AuthorizedElement permissions={[AppPermission.VisualInspectionRead]}>
                {searchResult?.visualInspections &&
                  searchResult?.visualInspections?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.VisualInspections) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.VisualInspections) || undefined
                      }
                      name='Visual inspections'
                      items={searchResult?.visualInspections?.items || []}
                    >
                      {(item) => (
                        <VisualInspectionLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.DamageDetectionRead]}>
                {searchResult?.damageDetections &&
                  searchResult?.damageDetections?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.DamageDetections) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.DamageDetections) || undefined
                      }
                      name='Damage detections'
                      items={searchResult?.damageDetections?.items || []}
                    >
                      {(item) => (
                        <DamageDetectionLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.DamageDetectionRead]}>
                {searchResult?.damageDetectionAggregates &&
                  searchResult?.damageDetectionAggregates?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.DamageDetectionAggregates) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.DamageDetectionAggregates) || undefined
                      }
                      name='Damage detections (aggregates)'
                      items={searchResult?.damageDetectionAggregates?.items || []}
                    >
                      {(item) => (
                        <DamageDetectionAggregateLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.InvoiceRead]}>
                {searchResult?.invoices &&
                  searchResult?.invoices?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Invoices) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Invoices) || undefined}
                      name='Invoices'
                      items={searchResult?.invoices?.items || []}
                    >
                      {(item) => (
                        <InvoiceLink
                          invoice={item}
                          withIcon
                          withTooltip={false}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.AccessoryCheckRead]}>
                {searchResult?.accessoryChecks &&
                  searchResult?.accessoryChecks?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.AccessoryChecks) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.AccessoryChecks) || undefined
                      }
                      name='Accessory checks'
                      items={searchResult?.accessoryChecks?.items || []}
                    >
                      {(item) => (
                        <AccessoryCheckLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.AssetRead]}>
                {searchResult?.assets &&
                  searchResult?.assets?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Assets) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Assets) || undefined}
                      name='Assets'
                      items={searchResult?.assets?.items || []}
                    >
                      {(item) => (
                        <AssetLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.CustomerRead]}>
                {searchResult?.customers &&
                  searchResult?.customers?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Customers) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Customers) || undefined}
                      name='Customers'
                      items={searchResult?.customers?.items || []}
                    >
                      {(item) => (
                        <CustomerLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.AssessmentFlowRead]}>
                {searchResult?.assessmentFlows &&
                  searchResult?.assessmentFlows?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.AssessmentFlows) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.AssessmentFlows) || undefined
                      }
                      name='Damages & approvals'
                      items={searchResult?.assessmentFlows?.items || []}
                    >
                      {(item) => (
                        <AssessmentFlowLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.DamageCostEvaluationRead]}>
                {searchResult?.damageCostEvaluations &&
                  searchResult?.damageCostEvaluations?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.DamageCostEvaluations) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.DamageCostEvaluations) || undefined
                      }
                      name='Damage cost evaluations'
                      items={searchResult?.damageCostEvaluations?.items || []}
                    >
                      {(item) => (
                        <DamageCostEvaluationLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.DamageCostEvaluationRead]}>
                {searchResult?.damageCostEvaluationAggregates &&
                  searchResult?.damageCostEvaluationAggregates?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.DamageCostEvaluationAggregates) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.DamageCostEvaluationAggregates) ||
                        undefined
                      }
                      name='Damage cost evaluation (aggregates)'
                      items={searchResult?.damageCostEvaluationAggregates?.items || []}
                    >
                      {(item) => (
                        <DamageCostEvaluationAggregateLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.RepairOperationRead]}>
                {searchResult?.repairOperations &&
                  searchResult?.repairOperations?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.RepairOperations) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.RepairOperations) || undefined
                      }
                      name='Repair operations'
                      items={searchResult?.repairOperations?.items || []}
                    >
                      {(item) => (
                        <RepairOperationLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.DepartmentRead]}>
                {searchResult?.departments &&
                  searchResult?.departments?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Departments) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Departments) || undefined}
                      name='Departments'
                      items={searchResult?.departments?.items || []}
                    >
                      {(item) => (
                        <DepartmentLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.LocationRead]}>
                {searchResult?.locations &&
                  searchResult?.locations?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Locations) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Locations) || undefined}
                      name='Locations'
                      items={searchResult?.locations?.items || []}
                    >
                      {(item) => (
                        <LocationLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.SpotRead]}>
                {searchResult?.spots &&
                  searchResult?.spots?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Spots) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Spots) || undefined}
                      name='Spots'
                      items={searchResult?.spots?.items || []}
                    >
                      {(item) => (
                        <SpotLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.ProductLocationRead]}>
                {searchResult?.productLocations &&
                  searchResult?.productLocations?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.ProductLocations) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.ProductLocations) || undefined
                      }
                      name='Sales locations'
                      items={searchResult?.productLocations?.items || []}
                    >
                      {(item) => (
                        <ProductLocationLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.PoolRead]}>
                {searchResult?.pools &&
                  searchResult?.pools?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.Pools) && (
                    <CategoryBlock
                      actionAsItem={loadMoreButton(GlobalSearchCategory.Pools) || undefined}
                      name='Pools'
                      items={searchResult?.pools?.items || []}
                    >
                      {(item) => (
                        <PoolLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.AssetSubscriptionPlanRead]}>
                {searchResult?.assetSubscriptionPlans &&
                  searchResult?.assetSubscriptionPlans?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.AssetSubscriptionPlans) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.AssetSubscriptionPlans) || undefined
                      }
                      name='Asset subscription plans'
                      items={searchResult?.assetSubscriptionPlans?.items || []}
                    >
                      {(item) => (
                        <AssetSubscriptionPlanLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.AssetSubscriptionRead]}>
                {searchResult?.assetSubscriptions &&
                  searchResult?.assetSubscriptions?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.AssetSubscriptions) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.AssetSubscriptions) || undefined
                      }
                      name='Asset subscriptions'
                      items={searchResult?.assetSubscriptions?.items || []}
                    >
                      {(item) => (
                        <AssetSubscriptionLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.TenantRequestRead]}>
                {searchResult?.tenantRequests &&
                  searchResult?.tenantRequests?.items?.length !== 0 &&
                  shouldShowCategoryResult(GlobalSearchCategory.TenantRequests) && (
                    <CategoryBlock
                      actionAsItem={
                        loadMoreButton(GlobalSearchCategory.TenantRequests) || undefined
                      }
                      name='Company requests'
                      items={searchResult?.tenantRequests?.items || []}
                    >
                      {(item) => (
                        <TenantRequestLink
                          entity={item}
                          inlineProps={defaultInlineProps}
                          onClick={closePanel}
                        />
                      )}
                    </CategoryBlock>
                  )}
              </AuthorizedElement>

              {/* {!shouldShowCategoryResult(GlobalSearchCategory.All) && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button onClick={handleLoadMore}>More</Button>
                </Box>
              )} */}
            </Stack>
          )}
          {!searchResult && !request.isLoading && (
            <NoDataAlert
              title='Type at least 3 symbols to start searching across the system.'
              description='You can select a specific search category to get more accurate results.'
            />
          )}
        </Box>
      )}
    </Box>
  );
}
